import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Grid,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Link } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import unifiedApi from '../../utils/unifiedApi';
import { useAuth } from '../../contexts/AuthContext';

const TeamLeadingScorers = ({ 
  teamId, 
  _leagueId, // No longer needed with unified endpoint but kept for compatibility
  teamSlug, 
  _isPublicView = false, // No longer needed with unified endpoint but kept for compatibility
  title = "Leading Scorers",
  limit = 10,
  minimumGoals = 0,
  hidePieChartOnMobile = false
}) => {
  const [scorersData, setScorersData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const fetchLeadingScorers = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Use team slug if provided, otherwise fall back to teamId
        const teamKey = teamSlug || (teamId ? teamId.toString() : null);
        
        if (!teamKey) {
          throw new Error('Team slug or team ID is required');
        }
        
        // Use unified API which handles both authenticated and anonymous users automatically
        const data = await unifiedApi.getTeamLeadingScorers(teamKey, limit, minimumGoals);
        setScorersData(data);
      } catch (err) {
        console.error('Error fetching team leading scorers:', err);
        setError('Failed to load leading scorers');
      } finally {
        setLoading(false);
      }
    };

    fetchLeadingScorers();
  }, [teamId, teamSlug, limit, minimumGoals]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error loading leading scorers: {error}
      </Alert>
    );
  }

  if (!scorersData || !scorersData.scorers || scorersData.scorers.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No goal scorers found for this team.
      </Alert>
    );
  }

  // Prepare data for pie chart
  const pieData = scorersData.scorers.map((scorer, index) => ({
    name: `${scorer.firstName} ${scorer.lastName}`,
    value: scorer.goalsScored,
    percentage: scorer.goalPercentage,
    // Generate colors for the pie chart
    color: `hsl(${(index * 137.5) % 360}, 70%, 50%)`
  }));

  // Custom tooltip for pie chart
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box 
          sx={{ 
            backgroundColor: 'white', 
            border: '1px solid #ccc', 
            borderRadius: 1, 
            p: 1,
            boxShadow: 2
          }}
        >
          <Typography variant="body2" fontWeight="medium">
            {data.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Goals: {data.value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Team %: {data.percentage}%
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ color: '#2E4156', fontWeight: 'bold' }}>
        {title}
      </Typography>
      
      <Grid container spacing={3}>
        {/* Left side - Table (full width on mobile when chart hidden, 50% otherwise) */}
        <Grid size={{ xs: 12, md: (isMobile && hidePieChartOnMobile) ? 12 : 6 }}>
          <TableContainer sx={{ maxHeight: isMobile ? 300 : 450 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Player Name</TableCell>
                  <TableCell align="center">Goals</TableCell>
                  <TableCell align="center">Team %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scorersData.scorers.map((scorer) => (
                  <TableRow key={scorer.userTenantId} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {scorer.position}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isAuthenticated && scorer.userTenantId ? (
                          <Typography 
                            component={Link}
                            to={`/player/${scorer.userTenantId}`}
                            variant="body2"
                            sx={{
                              textDecoration: 'none',
                              color: 'inherit',
                              '&:hover': {
                                color: 'primary.main',
                                textDecoration: 'underline'
                              }
                            }}
                          >
                            {scorer.firstName} {scorer.lastName}
                          </Typography>
                        ) : (
                          <Typography variant="body2">
                            {scorer.firstName} {scorer.lastName}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="medium">
                        {scorer.goalsScored}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" color="text.secondary">
                        {scorer.goalPercentage}%
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        {/* Right side - Pie Chart (hidden on mobile if hidePieChartOnMobile is true) */}
        {!(isMobile && hidePieChartOnMobile) && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ height: { xs: 350, md: 450 }, width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={30}
                    outerRadius={isMobile ? 80 : 120}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend 
                    verticalAlign="bottom" 
                    height={isMobile ? 40 : 50}
                    wrapperStyle={{
                      fontSize: isMobile ? '10px' : '12px',
                      paddingTop: '10px'
                    }}
                    formatter={(value, entry) => (
                      <span style={{ fontSize: isMobile ? '10px' : '12px' }}>
                        {value} ({entry.payload.value})
                      </span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Metadata */}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
        Total scorers: {scorersData.totalScorers} | Generated: {new Date(scorersData.generatedAt).toLocaleString()}
      </Typography>
    </Box>
  );
};

export default TeamLeadingScorers;